// TODO @flow @auth is there a reason for this file to exist standalone?
import {Dispatchable} from "../../stem-core/base/Dispatcher";
import type {TimeoutHandler} from "../../stem-core/base/Utils";
import {authService, AuthService} from "../../client/connection/services/AuthService";
import {userService} from "../../client/connection/services/UserService.js";
import {iFrameConnection} from "./IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {GlobalState} from "../../stem-core/state/State";
import {AuthToken, type SessionToken} from "../../client/connection/AuthToken";
import {apiClient} from "../../client/connection/BlinkApiClient.js";
import {apiUserEditMerchantMetadata} from "../../client/state/UserProfileStore.js";

// How long an app told to wait for another app's fetch gives it before fetching for itself.
const SHARED_USER_DATA_TIMEOUT = 5000;
// Set while we're waiting on another app's fetch. Also what tells us an incoming USER_DATA_FETCHED is meant
// for us: sendToIFrames posts to every frame including the sender, and the app that fetched must not
// re-apply its own broadcast.
let sharedUserDataTimeout: TimeoutHandler = null;

// Whoever fetched the user data for a new merchant token hands it to the apps waiting on it. A null is an
// answer too - it releases them to fetch for themselves.
function shareUserData() {
    iFrameConnection.sendToIFrames(IFrameMessages.USER_DATA_FETCHED, {userData: userService.userData});
}

// Swallows the error: the caller's job is to answer the apps waiting on it, and a null is an answer.
async function refetchUserData() {
    try {
        await userService.forceFetchUserData();
    } catch (error) {
        console.error("[Blink] Failed to fetch the user data for the merchant token", error);
    }
}

// A user data payload that arrived over postMessage rather than over Ajax, so it hasn't been through the
// api client's state postprocessor.
function applyUserData(userData: Record<string, any>) {
    if (userData.state) {
        GlobalState.importState(userData.state);
    }
    userService.setUserData(userData);
}

function applySharedUserData(userData: Record<string, any> | null) {
    if (!sharedUserDataTimeout) {
        return; // Not waiting on anyone - this is our own broadcast coming back off sendToIFrames.
    }
    clearTimeout(sharedUserDataTimeout);
    sharedUserDataTimeout = null;
    if (userData) {
        applyUserData(userData);
    } else {
        refetchUserData(); // The app that fetched came back empty, so nobody else will.
    }
}

export async function shiftToMerchantToken(fetchUserData = true) {
    const userPreviouslyAuthenticated = authService.isAuthenticated();
    // If no user data request went out yet we're on the app init path, which is about to make
    // that request with the token we set below.
    const alreadyRequestedUserData = userService.initiatedUserDataRequest;
    const {key, expiresAt} = iFrameAuthService.merchantToken;
    authService.token.setToken(key, expiresAt, true);

    if (!alreadyRequestedUserData) {
        if (fetchUserData) {
            // That request goes out with the token we just set, so its response is the one to share.
            userService.onUserDataRequestFinished(shareUserData);
        }
        return;
    }

    if (userPreviouslyAuthenticated) {
        apiClient.dropWebsocketConnection();
    }
    if (!fetchUserData) {
        // Another app is fetching and shares the response with us. It can end up not fetching at all
        // though - it may have been torn down, or its own fetch may have failed - and we've already
        // swapped our token, so don't sit on the previous user's data forever waiting.
        clearTimeout(sharedUserDataTimeout);
        sharedUserDataTimeout = setTimeout(() => {
            sharedUserDataTimeout = null;
            refetchUserData(); // The app that was going to share never did.
        }, SHARED_USER_DATA_TIMEOUT);
        return;
    }

    // The data we have is for another user, or for none if the token arrived after init (a
    // setToken blink action). Nothing else refetches it, since ensureUserDataRequested() is a
    // no-op once a request has finished.
    await refetchUserData();
    // TODO Here we used to also remove non-merchant specific data from stores, now we don't
    shareUserData();
}

class IFrameAuthService extends Dispatchable {
    merchantToken: SessionToken | null = null;

    setMerchantToken(token: SessionToken | null, fetchUserData = true) {
        this.merchantToken = token;
        // TODO @Denis @Mihai this logic is implicit, make it explicit (like, if the token is full or merchant)
        // TODO @Mihai @Denis always use a token object with t, e
        if (token && (AuthToken.getUserIdFromToken(token.key) !== authService.token.getUserId())) {
            shiftToMerchantToken(fetchUserData);
        }
    }

    init(token: SessionToken | null) {
        authService.addListener(AuthService.EventTypes.SET_MERCHANT_TOKEN, (token: SessionToken) => {
            this.setMerchantToken(token);
            iFrameConnection.sendToSDK(IFrameMessages.SET_MERCHANT_TOKEN, {token});
        });

        iFrameConnection.addListener(IFrameMessages.EDIT_MERCHANT_USER_METADATA, async ({merchantId, metadata}: {merchantId: number; metadata: Record<string, any>}) => {
            await apiUserEditMerchantMetadata(merchantId, metadata);
        })

        iFrameConnection.addListener(IFrameMessages.UPDATE_MERCHANT_TOKEN, ({token, fetchUserData}: {token: SessionToken | null; fetchUserData: boolean}) => {
            this.setMerchantToken(token, fetchUserData);
        });

        // Sent by whichever iframe app fetched the user data for a new token, to the apps waiting on it.
        iFrameConnection.addIframesListener(IFrameMessages.USER_DATA_FETCHED, ({userData}: {userData: Record<string, any> | null}) => applySharedUserData(userData));
        iFrameConnection.addIframesListener(IFrameMessages.USER_TOKEN_UPDATE, ({token, merchantToken, userData, doNotRebroadcast}) => {
            if (!token) {
                authService.token.clear();
            } else {
                const {t, e} = JSON.parse(token);
                authService.token.setToken(t, e);
            }

            if (merchantToken) {
                merchantToken = {
                    key: JSON.parse(merchantToken).t,
                    expiresAt: JSON.parse(merchantToken).e,
                }
                this.setMerchantToken(merchantToken);
                iFrameConnection.sendToSDK(IFrameMessages.SET_MERCHANT_TOKEN, {token: merchantToken});
            }

            if (userData) {
                applyUserData(userData);
            }

            if (!doNotRebroadcast) {
                iFrameConnection.sendToIFrames(IFrameMessages.USER_TOKEN_UPDATE, {
                    token,
                    userData,
                    doNotRebroadcast: true,
                });
            }
        });

        this.setMerchantToken(token);
    }
}

export const iFrameAuthService = new IFrameAuthService();
